// CatalogCardTile — Kachel des Karten-Katalogs. Das Kartenbild trägt die Information:
// Name/Typ/Preis liest man auf der Karte selbst oder im Karten-Detail (Klick auf die
// Kachel). Einzige Ausnahme sind die Begründungs-Chips des Vorschläge-Reiters — sie
// stehen nicht auf der Karte und füllen deshalb den Info-Bereich der Kachel.
import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { BellOff, Plus, X } from 'lucide-react';
import Badge from '../../../ui/Badge';
import ButtonSpinner from '../../../ui/ButtonSpinner';
import CardOverlays from '../../../ui/CardOverlays';
import CardTile from '../../../ui/CardTile';
import ContextMenu from '../../../ui/ContextMenu';
import { catalogCardDragId } from '../utils/dragCardUtils';
import './CardCatalog.css';
import './suggestionReasons.css';

/**
 * @param {object} entry — Treffer aus `GET /api/cards/search` (CardSearchEntryDTO)
 * @param {boolean} canAdd — Plus-Knopf zeigen
 * @param {boolean} adding — Add läuft gerade: Plus zeigt einen Spinner, die Kachel tritt
 *   zurück. Der Knopf bleibt fokussierbar und trägt `aria-busy` statt `disabled` — ein
 *   gesperrter Knopf verliert den Fokus, und mit ihm verschwände die per `:focus-within`
 *   eingeblendete Knopfgruppe samt Spinner unter dem Mauszeiger
 * @param {(entry) => void} onOpen — Klick auf die Kachel (Karten-Detail)
 * @param {(entry) => void} onAdd — Klick auf das Plus
 * @param {boolean} dndEnabled — Kachel darf als Drag-Quelle dienen; den Zieh-Kontext
 *   stellt der Rahmen, in dem der Katalog steckt. Die Zieh-Anmeldung steckt in einer
 *   eigenen Hülle, die nur dann rendert — ohne DndContext (eigenes Fenster) gäbe es
 *   sonst eine Anmeldung ins Leere
 * @param {{tagId: string, label: string, description: string|null}[]} reasons — Themen,
 *   wegen derer die Karte vorgeschlagen wird; ein Array (auch leer) zeigt den Info-Bereich in
 *   fester Zwei-Zeilen-Höhe, `null` (Suche-Reiter) zeigt ihn gar nicht. Sie füllen
 *   `CardTile.Info` — was über zwei Chip-Zeilen hinausgeht, schneidet die Kachel ab
 * @param {(tagId: string) => void} onMuteTag — Thema stummschalten (Menü hinter dem Chip)
 * @param {(entry) => void} onHide — ✕: diese Karte nicht mehr vorschlagen; fehlt der
 *   Callback, gibt es den Knopf nicht (Suche-Reiter)
 * @param {boolean} hiding — Ausblenden läuft gerade; wirkt auf das ✕ wie `adding` aufs Plus
 */
export default function CatalogCardTile({
  entry,
  canAdd = false,
  adding = false,
  onOpen,
  onAdd,
  dndEnabled = false,
  reasons = null,
  onMuteTag,
  onHide,
  hiding = false,
}) {
  const gruende = reasons || [];
  const beschäftigt = adding || hiding;

  const Huelle = dndEnabled ? DraggableTileShell : PlainTileShell;

  return (
    <Huelle entry={entry} busy={beschäftigt}>
      <CardTile onClick={() => onOpen?.(entry)} clickLabel={`${entry.name} — Details`} scryfallId={dndEnabled ? undefined : entry.id}>
        <CardTile.Image
          src={entry.imageUrl}
          backSrc={entry.imageUrlBack || undefined}
          alt={entry.name}
          orientation={entry.imageOrientation}
        >
          <CardOverlays>
            {entry.ownedQuantity > 0 && (
              <CardOverlays.Corner position="top-left">
                <CardOverlays.Badge passive>
                  <span
                    className="card-catalog__owned-dot"
                    role="img"
                    aria-label="In deiner Sammlung"
                  />
                </CardOverlays.Badge>
              </CardOverlays.Corner>
            )}
            {(canAdd || typeof onHide === 'function') && (
              <CardOverlays.Actions>
                {canAdd && (
                  <CardOverlays.Button
                    icon={adding ? ButtonSpinner : Plus}
                    label={`${entry.name} hinzufügen`}
                    aria-busy={beschäftigt || undefined}
                    onClick={() => { if (!beschäftigt) onAdd?.(entry); }}
                  />
                )}
                {typeof onHide === 'function' && (
                  <CardOverlays.Button
                    icon={hiding ? ButtonSpinner : X}
                    label={`${entry.name} nicht vorschlagen`}
                    aria-busy={beschäftigt || undefined}
                    onClick={() => { if (!beschäftigt) onHide(entry); }}
                  />
                )}
              </CardOverlays.Actions>
            )}
          </CardOverlays>
        </CardTile.Image>
        {Array.isArray(reasons) && (
          <CardTile.Info>
            <CardTile.Row>
              <span className="suggestion-reasons">
                {gruende.map((grund) => (
                  <ContextMenu
                    key={grund.tagId}
                    trigger={<Badge button title={grund.description || undefined}>{grund.label}</Badge>}
                  >
                    <ContextMenu.Item icon={BellOff} onSelect={() => onMuteTag?.(grund.tagId)}>
                      Thema stummschalten
                    </ContextMenu.Item>
                  </ContextMenu>
                ))}
              </span>
            </CardTile.Row>
          </CardTile.Info>
        )}
      </CardTile>
    </Huelle>
  );
}

function PlainTileShell({ busy, children }) {
  return <div className={tileClasses(busy)}>{children}</div>;
}

/** Klassen des Kachel-Rahmens; `busy` dimmt die Kachel, solange ihre Aktion läuft. */
function tileClasses(busy, extra = '') {
  return `card-catalog__tile${extra}${busy ? ' card-catalog__tile--busy' : ''}`;
}

/**
 * Zieh-Hülle der Kachel. Eigene Komponente statt eines Hooks mit Schalter: `useDraggable`
 * darf nur laufen, wo ein `DndContext` steht — der Rahmen entscheidet das über `dndEnabled`.
 * Die Zieh-Griffe sitzen auf der ganzen Kachel; Klick und die Overlay-Knöpfe bleiben
 * bedienbar, weil der Maus-Sensor des Editors erst nach 5px Weg auslöst.
 */
function DraggableTileShell({ entry, busy, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: catalogCardDragId(entry.id),
    data: { type: 'catalog-card', entry },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-drag-id={catalogCardDragId(entry.id)}
      className={tileClasses(busy, ` card-catalog__tile--draggable${isDragging ? ' is-dragging' : ''}`)}
    >
      {children}
    </div>
  );
}
